// src/lib/http.js
// Updated: production-safe computeBase() + Netlify compatibility

// src/lib/http.js
// Updated: production-safe computeBase() + Netlify compatibility


function computeBase() {
  try {
    if (typeof window !== "undefined") {
      const h = (window.location.hostname || "").toLowerCase();
      if (h.endsWith(".netlify.app") || h.endsWith("ehgezly.com") || h.endsWith("ehgezly.app")) {
        return ""; // Same origin → proxy (no CORS)
      }
      if (h === "localhost" || h === "127.0.0.1" || h.includes("localhost")) {
        return ""; // CRA dev-server proxy → same origin (cookies + auth work locally)
      }
    }
  } catch {}
  const env = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || "").trim();
  if (env) return env;
  return "http://localhost:3001";
}

export const API_BASE = computeBase().replace(/\/$/, "");

const FLY_API = "https://ehgez-api-v2.fly.dev";

function isHostedFrontend() {
  try {
    if (typeof window === "undefined") return false;
    const h = (window.location.hostname || "").toLowerCase();
    return h.endsWith(".netlify.app") || h.endsWith("ehgezly.com") || h.endsWith("ehgezly.app");
  } catch {
    return false;
  }
}

function defaultSocketUrl() {
  const fromEnv = (process.env.REACT_APP_SOCKET_URL || "").trim().replace(/\/$/, "");
  if (fromEnv) return fromEnv;
  try {
    if (typeof window !== "undefined") {
      const h = (window.location.hostname || "").toLowerCase();
      if (h === "localhost" || h === "127.0.0.1" || h.includes("localhost")) {
        return "http://localhost:3001";
      }
    }
  } catch {}
  // Same-origin (empty API_BASE) hits Netlify, which cannot proxy WebSockets.
  // Always talk to Fly for live booking events.
  return FLY_API;
}

export const SOCKET_URL = defaultSocketUrl();

function rewriteIfNeeded(p) {
  if (!p) return p;
  if (!p.startsWith("/")) p = `/${p}`;
  if (p.startsWith("/api/")) return p;

  const AUTH_PUBLIC = /^(\/login|\/logout|\/register|\/refresh|\/verify-phone|\/verify-email|\/resend-verification-email|\/send-otp|\/verify-otp|\/forgot-password|\/reset-password)(\/.*)?$/i;
  if (AUTH_PUBLIC.test(p)) return `/api${p}`;
  
  // Add booking endpoints to API rewrite (exclude business creation)
  const API_ENDPOINTS = /^(\/bookings|\/reviews|\/profile)(\?.*)?$/i;
  if (API_ENDPOINTS.test(p)) {
    return `/api${p}`;
  }
  
  // Handle businesses endpoint separately - only rewrite if it's not the creation endpoint
  if (p === '/businesses') {
    return p;
  }
  
  if (p.startsWith('/businesses/')) {
    return `/api${p}`;
  }
  
  return p;
}

function toUrl(p, { formUpload = false } = {}) {
  const rp = rewriteIfNeeded(p);
  if (/^https?:\/\//i.test(rp)) return rp;
  // Netlify's /api proxy times out or rejects multipart photo uploads (502 HTML),
  // which the admin form shows as "Could not save the business."
  if (formUpload && !API_BASE && isHostedFrontend()) return `${FLY_API}${rp}`;
  if (API_BASE) return `${API_BASE}${rp}`;
  return rp; // same-origin (Netlify proxy or CRA dev-server proxy)
}


// ----------------------------- CSRF (client channel) -------------------------
let CSRF_TOKEN = null;
const CSRF_PATHS = ["/csrf", "/api/csrf", "/auth/csrf"];
const REFRESH_PATHS = ["/api/auth/refresh", "/api/refresh", "/refresh"];

function getCookie(name) {
  try {
    const m = document.cookie.split("; ").find((r) => r.startsWith(name + "="));
    return m ? decodeURIComponent(m.split("=").slice(1).join("=")) : "";
  } catch { return ""; }
}

export async function ensureCsrf() {
  if (CSRF_TOKEN) return CSRF_TOKEN;

  const fromCookie = getCookie("XSRF-TOKEN");
  if (fromCookie) { CSRF_TOKEN = fromCookie; return CSRF_TOKEN; }

  for (const p of CSRF_PATHS) {
    try {
      const res = await fetch(toUrl(p), {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) continue;
      const data = await res.json().catch(() => null);
      if (data && data.csrfToken) { CSRF_TOKEN = data.csrfToken; return CSRF_TOKEN; }
      const again = getCookie("XSRF-TOKEN");
      if (again) { CSRF_TOKEN = again; return CSRF_TOKEN; }
    } catch {}
  }
  return null;
}

// ----------------------------- Client token store ----------------------------
let AUTH_TOKEN = null;
let REFRESH_TOKEN = null;

try {
  if (typeof sessionStorage !== "undefined") {
    AUTH_TOKEN = sessionStorage.getItem("token") || null;
    REFRESH_TOKEN = sessionStorage.getItem("refreshToken") || null;
  }
  if (!AUTH_TOKEN && typeof localStorage !== "undefined") {
    AUTH_TOKEN = localStorage.getItem("token") || null;
  }
  if (!REFRESH_TOKEN && typeof localStorage !== "undefined") {
    REFRESH_TOKEN = localStorage.getItem("refreshToken") || null;
  }
} catch {}

export function setToken(token, { remember = false } = {}) {
  AUTH_TOKEN = token || null;
  try {
    if (remember) {
      if (token) localStorage.setItem("token", token);
      else localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    } else {
      localStorage.removeItem("token");
      if (token) sessionStorage.setItem("token", token);
      else sessionStorage.removeItem("token");
    }
  } catch {}
}

export function setRefreshToken(refreshToken, { remember = false } = {}) {
  REFRESH_TOKEN = refreshToken || null;
  try {
    if (remember) {
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
      else localStorage.removeItem("refreshToken");
      sessionStorage.removeItem("refreshToken");
    } else {
      localStorage.removeItem("refreshToken");
      if (refreshToken) sessionStorage.setItem("refreshToken", refreshToken);
      else sessionStorage.removeItem("refreshToken");
    }
  } catch {}
}

export function getToken() { return AUTH_TOKEN; }
export function getRefreshToken() { return REFRESH_TOKEN; }
export function clearToken() {
  AUTH_TOKEN = null;
  REFRESH_TOKEN = null;
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refreshToken");
  } catch {}
}

// ----------------------------- Refresh (client) ------------------------------
async function attemptRefresh() {
  const storedRt = getRefreshToken();
  let remember = false;
  try {
    remember = Boolean(localStorage.getItem("token") || localStorage.getItem("refreshToken"));
  } catch {}

  for (const p of REFRESH_PATHS) {
    try {
      const res = await fetch(toUrl(p), {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(CSRF_TOKEN ? { "X-XSRF-TOKEN": CSRF_TOKEN, "X-CSRF-Token": CSRF_TOKEN } : {}),
        },
        body: JSON.stringify(storedRt ? { refreshToken: storedRt } : {}),
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.accessToken) {
          setToken(data.accessToken, { remember });
          if (data.refreshToken) setRefreshToken(data.refreshToken, { remember });
          return true;
        }
      }
    } catch (error) {
      console.error('Refresh attempt failed:', error);
    }
  }
  return false;
}

/** Admin refresh uses HttpOnly refresh cookie + /api/admin/refresh → adm-token */
async function attemptAdminRefresh() {
  try {
    const res = await fetch(toUrl("/api/admin/refresh"), {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return false;
    const data = await res.json().catch(() => null);
    const token = data?.token || data?.accessToken || data?.admToken;
    if (!token || typeof localStorage === "undefined") return false;
    localStorage.setItem("adm-token", token);
    return true;
  } catch (error) {
    console.error("Admin refresh attempt failed:", error);
    return false;
  }
}

// ----------------------------- Helpers ---------------------------------------
function normalizeBearer(t) {
  if (!t) return "";
  return /^bearer\s+/i.test(t) ? t : `Bearer ${t}`;
}

function readTokenFromStorage() {
  if (AUTH_TOKEN) return AUTH_TOKEN;
  try {
    if (typeof sessionStorage !== "undefined") {
      const t = sessionStorage.getItem("token");
      if (t) return t;
    }
    if (typeof localStorage !== "undefined") {
      const t = localStorage.getItem("token");
      if (t) return t;
    }
  } catch {}
  return null;
}

// ----------------------------- core fetch ------------------------------------
async function coreFetch(path, init = {}, { admin = false, retry = true } = {}) {
  const method = (init.method || "GET").toUpperCase();
  const isForm = typeof FormData !== "undefined" && init.body instanceof FormData;

  // dynamic token (fresh per call)
  const dynamicToken = readTokenFromStorage();

  if (!admin && method !== "GET" && !dynamicToken && !CSRF_TOKEN) {
    await ensureCsrf().catch(() => {});
  }

  const headers = {
    ...(isForm ? {} : { "Content-Type": "application/json" }),
    ...(init.headers || {}),
  };

  if (!admin && method !== "GET" && !dynamicToken && CSRF_TOKEN) {
    headers["X-XSRF-TOKEN"] = CSRF_TOKEN;
    headers["X-CSRF-Token"] = CSRF_TOKEN;
  }

  if (!admin && dynamicToken) headers["Authorization"] = normalizeBearer(dynamicToken);

  if (admin && typeof localStorage !== "undefined") {
    const raw = localStorage.getItem("adm-token") || "";
    if (raw) {
      headers["Authorization"] = normalizeBearer(raw);
      headers["X-Admin-Token"] = raw;
      headers["X-Auth-Token"]  = raw;
    }
  }

  const res = await fetch(toUrl(path, { formUpload: isForm }), {
    credentials: "include",
    ...init,
    headers,
  });

  if (res.status === 401 && retry) {
    if (admin) {
      const refreshed = await attemptAdminRefresh();
      if (refreshed) return coreFetch(path, init, { admin, retry: false });
    } else {
      const refreshed = await attemptRefresh();
      if (refreshed) return coreFetch(path, init, { admin, retry: false });
    }
  }

  return res;
}

// ----------------------------- JSON helpers ----------------------------------
async function parseJsonSafely(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();

  if (res.status === 204 || res.status === 205) return {};

  if (ct.includes("application/json")) {
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      // Extract user-friendly error message from structured error format
      let errorMessage = res.statusText || "Request failed";
      
      if (data) {
        // Handle structured error format with details object
        if (data.details && (data.details.detail || data.details.title)) {
          errorMessage = data.details.detail || data.details.title;
        }
        // Fallback to message, error, or details.message
        else if (data.message) {
          errorMessage = data.message;
        }
        else if (data.error) {
          // If error is a code like "ERROR_BAD_REQUEST", try to get a better message
          if (data.error.startsWith("ERROR_")) {
            errorMessage = data.error.replace(/^ERROR_/, "").replace(/_/g, " ").toLowerCase();
            errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
          } else {
            errorMessage = data.error;
          }
        }
      }
      
      const err = new Error(errorMessage);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  const text = await res.text().catch(() => "");
  const hint = res.status === 404
    ? " (Hint: check Netlify _redirects ordering and that /api/* proxies are present)"
    : "";
  const err = new Error(`Non-JSON response (${res.status})${hint}`);
  err.status = res.status;
  err.body = text;
  throw err;
}

async function get(path)        { return parseJsonSafely(await coreFetch(path)); }
async function post(path, body) { return parseJsonSafely(await coreFetch(path,  { method: "POST",  body: JSON.stringify(body) })); }
async function put(path, body)  { return parseJsonSafely(await coreFetch(path,  { method: "PUT",   body: JSON.stringify(body) })); }
async function patch(path, body){ return parseJsonSafely(await coreFetch(path,  { method: "PATCH", body: JSON.stringify(body) })); }
async function del(path)        { return parseJsonSafely(await coreFetch(path,  { method: "DELETE" })); }

// ----------------------------- Public API ------------------------------------
export const http = {
  API_BASE,
  SOCKET_URL,
  toUrl,
  fetch: (path, init = {}) => coreFetch(path, init),
  get, post, put, patch, del,
  getJson: get,
  postJson: post,
  ensureCsrf,
  primeCsrf: ensureCsrf,
  setToken,
  getToken,
  setRefreshToken,
  getRefreshToken,
  clearToken,
  attemptRefresh,
};

// Add this debug function to help troubleshoot
export const debugHttp = () => {
  console.log('=== HTTP DEBUG INFO ===');
  console.log('API_BASE:', API_BASE);
  console.log('SOCKET_URL:', SOCKET_URL);
  console.log('Window location:', window.location.href);
  console.log('Environment:', process.env.NODE_ENV);
  console.log('======================');
};

/**
 * Public admin auth (login / MFA / forgot / reset) — never attach adm-token.
 * A stale Authorization header can force authenticate() → 401 Unauthorized
 * before the public route handler runs (or after fallthrough to authorize).
 */
async function adminPublicFetch(path, init = {}) {
  const method = (init.method || "GET").toUpperCase();
  const isForm = typeof FormData !== "undefined" && init.body instanceof FormData;
  const headers = {
    ...(isForm ? {} : { "Content-Type": "application/json" }),
    ...(init.headers || {}),
  };
  // Explicitly strip any auth the caller may have passed
  delete headers.Authorization;
  delete headers.authorization;
  delete headers["X-Admin-Token"];
  delete headers["X-Auth-Token"];

  return fetch(toUrl(path, { formUpload: isForm }), {
    credentials: "include",
    ...init,
    method,
    headers,
  });
}

// ----------------------------- Admin API -------------------------------------
export const httpAdmin = {
  fetch: (path, init = {}) => coreFetch(path, init, { admin: true }),
  /** Pre-auth endpoints — no Bearer / adm-token */
  publicFetch: (path, init = {}) => adminPublicFetch(path, init),
  get:    async (path)        => parseJsonSafely(await coreFetch(path, {}, { admin: true })),
  post:   async (path, body)  => parseJsonSafely(await coreFetch(path, { method: "POST",  body: JSON.stringify(body) }, { admin: true })),
  put:    async (path, body)  => parseJsonSafely(await coreFetch(path, { method: "PUT",   body: JSON.stringify(body) }, { admin: true })),
  patch:  async (path, body)  => parseJsonSafely(await coreFetch(path, { method: "PATCH", body: JSON.stringify(body) }, { admin: true })),
  del:    async (path)        => parseJsonSafely(await coreFetch(path, { method: "DELETE"                        }, { admin: true })),
  // Aliases to match http API for consistency
  getJson:  async (path)        => parseJsonSafely(await coreFetch(path, {}, { admin: true })),
  postJson: async (path, body)  => parseJsonSafely(await coreFetch(path, { method: "POST", body: JSON.stringify(body) }, { admin: true })),
  attemptRefresh: attemptAdminRefresh,
  logout: async () => {
    try {
      await coreFetch("/api/admin/logout", { method: "POST" }, { admin: true, retry: false });
    } catch {
      /* still clear local session */
    }
    try {
      localStorage.removeItem("adm-token");
      localStorage.removeItem("adm-user");
    } catch {}
  },
};

// Debug helper
if (typeof window !== "undefined") {
  window.__HTTP_DEBUG = () => {
    const out = {
      API_BASE,
      SOCKET_URL,
      origin: window.location.origin,
      hasClientTokenLocalStorage: (() => { try { return !!localStorage.getItem("token"); } catch { return null; } })(),
      hasClientTokenSession:      (() => { try { return !!sessionStorage.getItem("token"); } catch { return null; } })(),
      hasAdminToken:              (() => { try { return !!localStorage.getItem("adm-token"); } catch { return null; } })(),
      csrfPrimed: !!CSRF_TOKEN,
      xsrfCookie: getCookie("XSRF-TOKEN") ? "present" : "missing",
    };
    console.log("[http] debug →", out);
    return out;
  };
}